import { useRef, useState, useEffect, useCallback } from 'react';

/**
 * Custom hook to manage horizontal scrolling functionality, including navigation
 * button state and scroll behavior.
 *
 * @param {number} scrollStep - The distance to scroll on button click.
 * @returns {{
 * containerRef: React.RefObject<HTMLDivElement>,
 * prevButtonRef: React.RefObject<HTMLButtonElement>,
 * nextButtonRef: React.RefObject<HTMLButtonElement>,
 * isPrevDisabled: boolean,
 * isNextDisabled: boolean
 * }}
 */
const useHorizontalScroll = (scrollStep = 200) => {
    const containerRef = useRef(null);
    const prevButtonRef = useRef(null);
    const nextButtonRef = useRef(null);

    const [isPrevDisabled, setIsPrevDisabled] = useState(true);
    const [isNextDisabled, setIsNextDisabled] = useState(true);

    const updateButtonStates = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;

        const { scrollLeft, scrollWidth, clientWidth } = container;

        const hasOverflow = scrollWidth > clientWidth;

        if (hasOverflow) {
            const isAtStart = scrollLeft <= 2;
            const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 2;

            setIsPrevDisabled(isAtStart);
            setIsNextDisabled(isAtEnd);
        } else {
            setIsPrevDisabled(true);
            setIsNextDisabled(true);
        }
    }, []);

    const scrollBy = (amount) => {
        containerRef.current?.scrollBy({
            left: amount,
            behavior: 'smooth'
        });
    };

    const handlePrevClick = (e) => {
        e.preventDefault();
        scrollBy(-scrollStep);
    };

    const handleNextClick = (e) => {
        e.preventDefault();
        scrollBy(scrollStep);
    };

    useEffect(() => {
        const container = containerRef.current;

        if (container) {
            updateButtonStates();

            container.addEventListener('scroll', updateButtonStates);
            window.addEventListener('resize', updateButtonStates);
        }

        return () => {
            if (container) {
                container.removeEventListener('scroll', updateButtonStates);
            }
            window.removeEventListener('resize', updateButtonStates);
        };
    }, [updateButtonStates]);

    return {
        containerRef,
        prevButtonRef: { current: handlePrevClick },
        nextButtonRef: { current: handleNextClick },
        isPrevDisabled,
        isNextDisabled,
    };
};

export default useHorizontalScroll;